import 'reflect-metadata';
import { inject, injectable } from 'inversify-props';
import { IHttpClient } from './HttpClient';
import { Setting } from '../../models';

export interface ISettings {

	/** Gets the base URL to use for service calls. */
	baseUrl: string;

	/** Gets a value indicating if logging should be enabled in this application */
	enableLogging: boolean;

	/** Gets the prefix to use when storing things in local storage */
	storagePrefix: string;

	/** Gets the amount of time to wait before kicking off an auto complete search. */
	autoCompleteTimeout: number;
	autoCompleteListSize: number;
	maxSearchTextSize: number;
	confirmModalSettings: any;
	token: string;
	analyticsTrackingCode: string;
	seriesDelimiter: string;
	focusTimeout: number;

	seasonBaseUrl: string;
	catalogBaseUrl: string;
	flagBaseUrl: string;
	conditionBaseUrl: string;
	accountTypeListUrl: string;
	auditBaseUrl: string;
	accountTypeBaseUrl: string;
	disabledReasonBaseUrl: string;
	logEntryBaseUrl: string;
	genderBaseUrl: string;
	categoryUrl: string;
	contactMessageUrl: string;
	conditionListUrl: string;
	deckShapeUrl: string;
	deckSizeUrl: string;
	contactReasonBaseUrl: string;
	deckSurfaceUrl: string;
	truckMountUrl: string;
	artistBaseUrl: string;
	companyBaseUrl: string;
	proBaseUrl: string;
	technologyBaseUrl: string;
	deckBaseUrl: string;
	blogBaseUrl: string;
	deckImageBaseUrl: string;
	seriesBaseUrl: string;
	stanceBaseUrl: string;
	statsBaseUrl: string;
	userBaseUrl: string;
	roleBaseUrl: string;
	invitationBaseUrl: string;
	chartColors: string[];

	pageIndexParam: string;
	pageSizeParam: string;
	criteriaParam: string;
	yearStartParam: string;
	yearEndParam: string;
	categoryIdsParam: string;
	seriesIdsParam: string;
	proIdsParam: string;
	artistIdsParam: string;
	companyIdsParam: string;
	shapeIdsParam: string;
	surfaceIdsParam: string;
	sizeIdsParam: string;
	sortByParam: string;
	sortDirectionParam: string;

	deckMaxWidth: number;
	deckMaxLength: number;
	earliestYear: number;
	multiDeckLimit: number;
	minDecksForYearList: number;
	requireInviteForSignup: boolean;

	/** The year when decks went from old school truck mounts to new school truck mounts */
	truckMountDemarcationYear: number;


	get(id: string): Promise<Setting>;
	list(): Promise<Setting[]>;
	save(id: string, value: string): Promise<Setting>;

}

@injectable()
export class Settings implements ISettings {

	/** Gets the base URL to use for service calls. */
	public baseUrl: string = process.env.VUE_APP_SERVICE_URL;

	/** Gets a value indicating if logging should be enabled in this application */
	public enableLogging: boolean =  (process.env.VUE_APP_ENABLE_LOGGING === 'true');

	/** Gets the prefix to use when storing things in local storage */
	public storagePrefix: string = 'sl';

	/** Gets the amount of time to wait before kicking off an auto complete search. */
	public autoCompleteTimeout: number = 500;
	public autoCompleteListSize: number = 5;
	public maxSearchTextSize: number = 30;

	/** The amount of time in milliseconds that a view should wait before trying to autofocus a form element. */
	public focusTimeout: number = 200;

	/** the maxium width in inches that a skateboard can be set to. Decks shouldn't be wider than 30 inches. */
	public deckMaxWidth: number = 30;

	/** The maximum length in inches that a skateboard can be set to. Decks shouldn't be longer than 6 feet. */
	public deckMaxLength: number = 72;

	/** The minimum number of decks to have in a list before showing the year filter */
	public minDecksForYearList: number = 72;

	/** The maximum amount of decks that can be created with the multi add tool. This stops potential issues of creating hundres of decks by accident. */
	public multiDeckLimit: number = 30;

	/** The earliest year that can be set when creating decks. Skateboards didn't exist before this. */
	public earliestYear: number = 1950;

	/** The year when decks went from old school truck mounts to new school truck mounts */
	public truckMountDemarcationYear: number = 1992;

	/** The character to use when separating the series from the pro when naming a deck  */
	public seriesDelimiter: string = ' - ';

	/** A collection of colors to use when building charts */
	public chartColors: string[] = [
		'#FFC2CE',
		'#80B3FF',
		'#FD6E8A',
		'#A2122F',
		'#693726',
		'#FFBC1D',
		'#FBD93F',
		'#8A7E66',
		'#182945',
		'#963579',
		'#F168C3',
		'#F168C3',
		'#463F75',
		'#619161',
	];

	/** The token used to authenticate the current user */
	public token: string = 'IVBzr5Ies0UXXLYpgEq7ZPLDwRQ_Lj_nIvmpXGVtVKKomoCa02_dpdfjw5atkafJZZb01Lj7YdHzuQ89-ohk5CG_8K76ZlnEcPacCGouD5rLM5dwWQtfy_3koOI0A0asxKzCkdjk4na-9PBDtYJEwOtdUYRvse3jsEky0ZUuOl1dJklRCJy943ITu9iQTYltblWTRGSQ_H_v1O51OfCpCzspUTP94h8NX-aOtaNCvXWvFkOD6F5rqrkPhn7mIk8nXW9TFIHLZ8CvaWdoHVPbwYr47gRhXqut9CylKljh4PlpXJB8yCUfNN2kFjCzK8zzL1BQPunEU1vr_AGzTA8L-lEMwbV-oDcSA6Ny6Shjnx_tNX33y6T-IcWMgRDFqs5grTRXzMNkP7yFWZOJq8MIfdgZkZNAu2Mu-MYZcNil7e4GP1IX9qg19Ba_4IOkqo4HgEjtZ7C7D7jJnHSchjag-w';

	public analyticsTrackingCode: string = process.env.VUE_APP_ANALYTICS_TRACKING_CODE;

	public auditBaseUrl: string = '/api/audit';
	public flagBaseUrl: string = '/api/flags';
	public catalogBaseUrl: string = '/api/catalogs';
	public accountTypeListUrl: string = '/api/common/account-types';
	public disabledReasonBaseUrl: string = '/api/disabled-reasons';
	public logEntryBaseUrl: string = '/api/logs';
	public conditionBaseUrl: string = '/api/conditions';
	public contactReasonBaseUrl: string = '/api/contact-reasons';
	public contactMessageUrl: string = '/api/contact-messages';
	public seasonBaseUrl: string = '/api/seasons';
	public conditionListUrl: string = '/api/common/conditions';
	public categoryUrl: string = '/api/categories';
	public deckSurfaceUrl: string = '/api/deck-surfaces';
	public truckMountUrl: string = '/api/truck-mounts';
	public deckShapeUrl: string = '/api/deck-shapes';
	public technologyBaseUrl: string = '/api/technologies';
	public deckSizeUrl: string = '/api/deck-sizes';
	public accountTypeBaseUrl: string = '/api/account-types';
	public genderBaseUrl: string = '/api/genders';
	public artistBaseUrl: string = '/api/artists';
	public seriesBaseUrl: string = '/api/series';
	public companyBaseUrl: string = '/api/companies';
	public proBaseUrl: string = '/api/pros';
	public invitationBaseUrl: string = '/api/invitations';
	public blogBaseUrl: string = '/api/blogs';
	public deckBaseUrl: string = '/api/decks';
	public deckImageBaseUrl: string = '/api/deck-images';
	public statsBaseUrl: string = '/api/stats';
	public stanceBaseUrl: string = '/api/stances';
	public userBaseUrl: string = '/api/users';
	public roleBaseUrl: string = '/api/roles';


	public confirmModalSettings: any = {
		color: 'warning',
		icon: 'warning',
		title: 'Please Confirm',
	};


	public pageIndexParam: string = 'page';
	public pageSizeParam: string = 'pageSize';
	public criteriaParam: string = 'criteria';
	public yearStartParam: string = 'yearStart';
	public yearEndParam: string = 'yearEnd';
	public categoryIdsParam: string = 'categoryIds';
	public seriesIdsParam: string = 'seriesIds';
	public proIdsParam: string = 'proIds';
	public artistIdsParam: string = 'artistIds';
	public companyIdsParam: string = 'companyIds';
	public shapeIdsParam: string = 'shapeIds';
	public surfaceIdsParam: string = 'surfaceIds';
	public sizeIdsParam: string = 'sizeIds';
	public sortByParam: string = 'sort';
	public sortDirectionParam: string = 'dir';

	public requireInviteForSignup: boolean = true;


	constructor(@inject('IHttpClient') private httpClient: IHttpClient) { }



	
	public get(id: string): Promise<Setting> {
		const url = `${this.baseUrl}/api/settings/${id}`;
		return this.httpClient.get<Setting>(url);
	}
	
	public list(): Promise<Setting[]> {
		const url = `${this.baseUrl}/api/settings`;
		return this.httpClient.get<Setting[]>(url);
	}
	
	public save(id: string, value: string): Promise<Setting> {
		const url = `${this.baseUrl}/api/settings/${id}`;
		const payload = { id, value };
		return this.httpClient.put<Setting>(url, payload);
	}




}

